export interface CustomizableWatch {
  id: string;
  name: string;
  shortName: string;
  description: string;
  basePrice: number;
  image: string;
  customizerLink: string;
  customizationOptions: {
    caseMaterial?: string[];
    bezelType?: string[];
    dialColor?: string[];
    hands?: string[];
    braceletStrap?: string[];
    movement?: string[];
    pushers?: string[];
    subdialLayout?: string[];
    crownGuards?: string[];
    caseShape?: string[];
    braceletIntegration?: string[];
    dayDisplay?: string[];
  };
}

export const customizableWatches: CustomizableWatch[] = [
  {
    id: 'custom-sub-gmt',
    name: 'Create Your Own Sub/GMT',
    shortName: 'Sub/GMT',
    description: 'Design your perfect dive watch with GMT functionality. Choose from premium materials, bezel styles, and movement options to create a timepiece built for adventure.',
    basePrice: 2499,
    image: 'https://static.readdy.ai/image/64f7aba38830139cff78662b39218feb/a6250d97b72664656b60e264d017d709.png',
    customizerLink: '/product-customizer/custom-sub-gmt',
    customizationOptions: {
      caseMaterial: ['Stainless Steel', 'Titanium', 'Two-Tone', 'Yellow Gold', 'Rose Gold'],
      bezelType: ['Ceramic Black', 'Ceramic Blue', 'Ceramic Green', 'Aluminum Pepsi', 'Aluminum Batman'],
      dialColor: ['Black', 'Blue', 'Green', 'White', 'Sunburst Silver'],
      hands: ['Mercedes', 'Sword', 'Snowflake', 'Dauphine'],
      braceletStrap: ['Oyster Bracelet', 'Jubilee Bracelet', 'Rubber Strap', 'NATO Strap', 'Leather Strap'],
      movement: ['Automatic', 'GMT Automatic', 'High-Beat Automatic'],
    },
  },
  {
    id: 'custom-chronograph',
    name: 'Create Your Own Chronograph',
    shortName: 'Chronograph',
    description: 'Build your ultimate racing-inspired chronograph. Select case materials, pusher styles, subdial configurations, and more for precision timing in style.',
    basePrice: 2899,
    image: 'https://static.readdy.ai/image/64f7aba38830139cff78662b39218feb/3a0dd30d05593d74ebbd1728c4abc28b.png',
    customizerLink: '/product-customizer/custom-chronograph',
    customizationOptions: {
      caseMaterial: ['Stainless Steel', 'Titanium', 'Rose Gold', 'Platinum', 'Carbon Fiber'],
      pushers: ['Round Pushers', 'Rectangular Pushers', 'Screw-Down Pushers'],
      subdialLayout: ['Tri-Compax', 'Bi-Compax', 'Mono-Pusher'],
      dialColor: ['Black', 'White Panda', 'Blue', 'Silver', 'Carbon'],
      braceletStrap: ['Steel Bracelet', 'Racing Leather', 'Rubber Strap', 'Alligator Strap'],
      movement: ['Automatic Chronograph', 'Manual Chronograph', 'High-Frequency Chronograph'],
    },
  },
  {
    id: 'custom-44mm-diver',
    name: 'Create Your Own 44mm Diver',
    shortName: '44mm Diver',
    description: 'Craft your professional dive watch with a bold 44mm case. Choose materials, bezel options, and water resistance features for the ultimate underwater companion.',
    basePrice: 2699,
    image: 'https://static.readdy.ai/image/64f7aba38830139cff78662b39218feb/69ea43b56ce12499ca506cc38eaec880.png',
    customizerLink: '/product-customizer/custom-44mm-diver',
    customizationOptions: {
      caseMaterial: ['Stainless Steel', 'Titanium', 'Bronze', 'Black DLC', 'Two-Tone'],
      bezelType: ['Ceramic Black', 'Ceramic Blue', 'Ceramic Orange', 'Aluminum Silver', 'Sapphire Insert'],
      dialColor: ['Black', 'Blue', 'Orange', 'Green', 'White'],
      hands: ['Sword', 'Arrow', 'Plongeur', 'Baton'],
      braceletStrap: ['Steel Bracelet', 'Rubber Dive Strap', 'NATO Strap', 'Mesh Bracelet'],
      movement: ['Automatic 300m', 'Automatic 500m', 'High-Beat 1000m'],
    },
  },
  {
    id: 'custom-datejust',
    name: 'Create Your Own Datejust',
    shortName: 'Datejust',
    description: 'Design your classic dress watch with date function. Select from elegant materials, dial patterns, and bracelet styles for timeless sophistication.',
    basePrice: 3199,
    image: 'https://static.readdy.ai/image/64f7aba38830139cff78662b39218feb/28f39ec9800741bd8940d5d323cc480f.png',
    customizerLink: '/product-customizer/custom-datejust',
    customizationOptions: {
      caseMaterial: ['Stainless Steel', 'Two-Tone', 'Yellow Gold', 'White Gold', 'Everose Gold'],
      bezelType: ['Smooth', 'Fluted', 'Diamond', 'Engine-Turned'],
      dialColor: ['Champagne', 'Blue Sunburst', 'Black', 'Silver', 'Slate', 'Wimbledon'],
      hands: ['Dauphine', 'Baton', 'Roman', 'Diamond'],
      braceletStrap: ['Jubilee Bracelet', 'Oyster Bracelet', 'President Bracelet', 'Leather Strap'],
      movement: ['Automatic Date', 'Perpetual Automatic', 'COSC Certified'],
    },
  },
  {
    id: 'custom-santos',
    name: 'Create Your Own Santos',
    shortName: 'Santos',
    description: 'Build your iconic square aviation watch. Choose case finishes, bezel screws, dial styles, and integrated bracelet options for a distinctive look.',
    basePrice: 3499,
    image: 'https://static.readdy.ai/image/64f7aba38830139cff78662b39218feb/e1fbf8692233df6830a306be644c3744.png',
    customizerLink: '/product-customizer/custom-santos',
    customizationOptions: {
      caseMaterial: ['Stainless Steel', 'Two-Tone', 'Rose Gold', 'Yellow Gold', 'ADLC Black'],
      caseShape: ['Large 39mm', 'Medium 35mm', 'Extra Large 43mm'],
      dialColor: ['Silver', 'Blue', 'Black', 'Skeleton', 'Roman Numerals'],
      hands: ['Sword Blue', 'Sword Black', 'Baton', 'Skeleton'],
      braceletIntegration: ['Steel Integrated', 'Two-Tone Integrated', 'Leather Quick-Switch', 'Rubber Quick-Switch'],
      movement: ['Automatic', 'Skeleton Automatic', 'Manual Wind'],
    },
  },
  {
    id: 'custom-nautilus',
    name: 'Create Your Own Nautilus',
    shortName: 'Nautilus',
    description: 'Create your luxury sports watch with distinctive porthole design. Select premium materials, dial textures, and integrated bracelet styles.',
    basePrice: 3999,
    image: 'https://static.readdy.ai/image/64f7aba38830139cff78662b39218feb/cee8eab2b1be96dd846fdfae49c3ddff.png',
    customizerLink: '/product-customizer/custom-nautilus',
    customizationOptions: {
      caseMaterial: ['Stainless Steel', 'Two-Tone', 'Rose Gold', 'White Gold', 'Platinum'],
      dialColor: ['Blue Horizontal', 'Black Horizontal', 'White Horizontal', 'Brown Horizontal', 'Olive Green'],
      hands: ['Baton Luminous', 'Dauphine', 'Skeleton'],
      braceletIntegration: ['Steel Integrated', 'Two-Tone Integrated', 'Gold Integrated', 'Leather Strap', 'Rubber Strap'],
      movement: ['Ultra-Thin Automatic', 'Automatic Date', 'Automatic Power Reserve'],
    },
  },
  {
    id: 'custom-oak',
    name: 'Create Your Own Oak',
    shortName: 'Royal Oak',
    description: 'Design your octagonal luxury sports watch. Choose from premium materials, tapisserie dial patterns, and iconic integrated bracelet options.',
    basePrice: 4299,
    image: 'https://static.readdy.ai/image/64f7aba38830139cff78662b39218feb/568d14ec4aab7ed96275a3a3b9800ab5.png',
    customizerLink: '/product-customizer/custom-oak',
    customizationOptions: {
      caseMaterial: ['Stainless Steel', 'Two-Tone', 'Rose Gold', 'Yellow Gold', 'Titanium', 'Ceramic'],
      dialColor: ['Blue Tapisserie', 'Black Tapisserie', 'Silver Tapisserie', 'Grey Tapisserie', 'Salmon'],
      hands: ['Baton Luminous', 'Royal Oak Signature', 'Skeleton'],
      braceletIntegration: ['Steel Integrated', 'Two-Tone Integrated', 'Gold Integrated', 'Rubber Strap'],
      movement: ['Ultra-Thin Automatic', 'Automatic Chronograph', 'Perpetual Calendar'],
    },
  },
  {
    id: 'custom-daydate',
    name: 'Create Your Own Day-Date',
    shortName: 'Day-Date',
    description: 'Build your presidential watch with day and date display. Select precious metals, dial styles, and the iconic president bracelet for ultimate prestige.',
    basePrice: 4599,
    image: 'https://static.readdy.ai/image/64f7aba38830139cff78662b39218feb/d16204d14c3098e795c681dc4c18301d.png',
    customizerLink: '/product-customizer/custom-daydate',
    customizationOptions: {
      caseMaterial: ['Yellow Gold', 'White Gold', 'Everose Gold', 'Platinum'],
      bezelType: ['Fluted', 'Smooth', 'Diamond', 'Baguette Diamond'],
      dialColor: ['Champagne', 'Blue Sunburst', 'Green Ombre', 'Chocolate', 'Ice Blue', 'Olive Green'],
      dayDisplay: ['English', 'Arabic', 'Chinese', 'French', 'Spanish'],
      hands: ['Dauphine', 'Baton', 'Roman', 'Diamond'],
      braceletStrap: ['President Bracelet', 'Oyster Bracelet', 'Leather Strap'],
      movement: ['Perpetual Automatic', 'COSC Certified', 'Master Chronometer'],
    },
  },
];